import { useCallback, useEffect, useMemo, useState } from 'react'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { isOpen } from '../sync/projectors.ts'
import { TransportError } from '../sync/transport.ts'
import { businessCode } from '../ui/sensitive.tsx'

export interface PersonRow { id: string; display_name: string; preferred_locale: 'fr' | 'en'; kind: 'human' | 'system'; employment_state: 'active' | 'ended'; revision?: number }
export interface MembershipRow { id: string; site_id: string; person_id: string; role_code: string; valid_during: string }
export interface RoleRow { id: string; code: string; label: { fr: string; en: string } }

export const usePersons = () => {
  const rows = useMerged<PersonRow>('person', 'core.person')
  return useMemo(() => rows && rows.filter((p) => p.kind === 'human').sort((a, b) => a.display_name.localeCompare(b.display_name, 'fr')), [rows])
}
export const useMemberships = () => useMerged<MembershipRow>('membership', 'core.membership')
export const useRoles = () => useMerged<RoleRow>('role', 'core.role')

export const openRoles = (ms: MembershipRow[], personId: string): MembershipRow[] => ms.filter((m) => m.person_id === personId && isOpen(m))
export const roleLabel = (roles: RoleRow[], code: string, lang: string): string => { const r = roles.find((x) => x.code === code); return r ? (lang.startsWith('en') ? r.label.en : r.label.fr) : code }
export type PersonView = Pending<PersonRow>

// ---- what the administrator's RPCs return -----------------------------------------------------------------------------------------
export interface AdminFactor { factor_id: string; name: string | null; status: string; created_at: string; approved: boolean; revoked: boolean }
export interface AdminPerson {
  person_id: string; display_name: string; preferred_locale: string; employment_state: string; has_login: boolean; auth_user_id: string | null; has_pin: boolean
  memberships: Array<{ membership_id: string; site_id: string; role_code: string; since: string }>; factors: AdminFactor[]
}
export interface AdminDevice { device_id: string; label: string | null; site_id: string; auth_user_id: string; enrolled_at: string; revoked_at: string | null; last_seen_at: string | null; open_session: string | null }

/** The radio's own word beside the engine's (M23, docs/M23.md item 9): the engine learns it is offline from the browser's `offline` event (sync/scheduler.ts)
 *  or a failed send; `navigator.onLine` is read too, so a page opened with the radio off says so before anything is tried. */
const radioOn = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false

/** An online read of an RPC of schema `api` (the screens that are not mirrored). `online` is false when the device knows it has no network (the engine's word
 *  or the browser's); `error` is the server's business code, 'OFFLINE' when the call could not reach the server at all (a network failure, M23 item 9: the
 *  screen then says it is offline instead of blaming what it reads), 'UNKNOWN' otherwise. */
export function useRpc<T>(fn: string, args: Record<string, unknown>, enabled = true) {
  const { transport } = useClub()
  const online = useEngineState().online !== false && radioOn()
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const key = JSON.stringify(args)
  const load = useCallback(async () => {
    if (!enabled || !online) return
    try { setData(await transport.rpc<T>(fn, JSON.parse(key) as Record<string, unknown>)); setError(null); setLoadedFor(key) } catch (e) {
      setError(e instanceof TransportError && e.kind === 'network' ? 'OFFLINE' : businessCode(e) ?? 'UNKNOWN')
    }
  }, [transport, fn, key, enabled, online])
  useEffect(() => { void load() }, [load])
  // `data` is kept while new arguments load (no flicker); `fresh` says whether it answers the arguments now on screen (a verdict must, before a save is offered)
  return { data, error, reload: load, online, fresh: loadedFor === key }
}
